import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import YCodeThumbnail from '../components/YCodeThumbnail';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';

const REGISTRATION_FEE = 100;

const topics = [
  'Arrays & Strings',
  'Two Pointers & Sliding Window',
  'Binary Search',
  'Hashing',
  'Linked Lists',
  'Stacks & Queues',
  'Recursion & Backtracking',
  'Trees & BST',
  'Heaps',
  'Graphs',
  'Dynamic Programming',
  'Greedy & Intervals',
];

const DsaCrashCourse = () => {
  const { isAuthenticated, user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paying, setPaying] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);

  const checkRegistrationStatus = useCallback(async () => {
    if (!isAuthenticated()) {
      setIsRegistered(false);
      return;
    }
    setCheckingStatus(true);
    try {
      const response = await fetch(API_ENDPOINTS.INTERNSHIP.REGISTRATION_STATUS, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setIsRegistered(!!data.registered);
      }
    } catch (error) {
      console.error('Failed to check DSA crash course registration:', error);
    } finally {
      setCheckingStatus(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkRegistrationStatus();
  }, [checkRegistrationStatus, user]);

  useEffect(() => {
    if (showPaymentSuccess || showAuthModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPaymentSuccess, showAuthModal]);

  const openRazorpayCheckout = (orderData) => {
    const options = {
      key: orderData.keyId,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'YCodeverse',
      description: orderData.programName,
      order_id: orderData.orderId,
      prefill: {
        name: localStorage.getItem('username') || user?.username || '',
        email: localStorage.getItem('email') || user?.email || '',
      },
      theme: { color: '#f59e0b' },
      handler: async function (response) {
        try {
          const verifyResponse = await fetch(API_ENDPOINTS.INTERNSHIP.VERIFY_PAYMENT, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            }),
          });

          if (verifyResponse.ok) {
            const data = await verifyResponse.json();
            setPaymentDetails({
              programName: data.programName || orderData.programName,
              transactionId: response.razorpay_payment_id,
              amount: REGISTRATION_FEE,
            });
            setIsRegistered(true);
            setShowPaymentSuccess(true);
          } else {
            const errorData = await verifyResponse.json().catch(() => ({}));
            alert(
              errorData.error ||
                `Payment verification failed. Please contact support with Order ID: ${response.razorpay_order_id}`
            );
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          alert('Payment completed but verification failed. Please contact support.');
        } finally {
          setPaying(false);
        }
      },
      modal: {
        ondismiss: function () {
          setPaying(false);
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handleRegister = async () => {
    if (!isAuthenticated()) {
      setShowAuthModal(true);
      return;
    }
    if (isRegistered || paying) return;

    setPaying(true);
    try {
      const orderResponse = await fetch(API_ENDPOINTS.INTERNSHIP.CREATE_ORDER, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json().catch(() => ({}));
        alert(errorData.error || 'Failed to start payment. Please try again.');
        setPaying(false);
        return;
      }

      const orderData = await orderResponse.json();
      if (!window.Razorpay) {
        alert('Payment gateway failed to load. Please refresh the page and try again.');
        setPaying(false);
        return;
      }
      openRazorpayCheckout(orderData);
    } catch (error) {
      console.error('DSA crash course payment error:', error);
      alert('Failed to start payment. Please try again.');
      setPaying(false);
    }
  };

  const registerButtonClass =
    'inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100';

  const renderRegisterButton = (variant = 'primary') => {
    if (checkingStatus) {
      return (
        <button disabled className={`${registerButtonClass} bg-white/10 text-gray-300 border border-white/20`}>
          Checking status...
        </button>
      );
    }

    if (isRegistered) {
      return (
        <button
          disabled
          className={`${registerButtonClass} bg-green-500/20 text-green-300 border border-green-400/40`}
        >
          ✓ Registered for DSA Crash Course
        </button>
      );
    }

    if (variant === 'cta') {
      return (
        <button
          onClick={handleRegister}
          disabled={paying}
          className={`${registerButtonClass} px-10 bg-white text-gray-900 hover:bg-gray-100 shadow-xl`}
        >
          {paying ? 'Opening payment...' : `Join for ₹${REGISTRATION_FEE}`}
        </button>
      );
    }

    return (
      <button
        onClick={handleRegister}
        disabled={paying}
        className={`${registerButtonClass} bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white shadow-xl shadow-amber-500/30`}
      >
        {paying ? 'Opening payment...' : `Join for ₹${REGISTRATION_FEE}`}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#060d1a] text-white pt-20">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-amber-300 text-sm font-bold uppercase tracking-[0.2em] mb-4">
                DSA Crash Course
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.15] mb-6">
                The questions that get you placed, in three months.
              </h1>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                Starting 5 December. Three months of DSA, question by question, for ₹{REGISTRATION_FEE}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {renderRegisterButton('primary')}
                <a
                  href="#syllabus"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 font-semibold transition-colors"
                >
                  Topics
                </a>
              </div>
              {!isAuthenticated() && (
                <p className="text-gray-500 text-sm mt-3">Sign in to pay with Razorpay</p>
              )}
            </div>

            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-amber-500/40 via-orange-600/20 to-amber-500/30 blur-sm" />
              <div className="relative rounded-3xl border border-amber-400/30 bg-gradient-to-br from-[#1c1408] to-[#0a1628] p-8 sm:p-10 shadow-2xl">
                <p className="text-sm uppercase tracking-widest text-amber-300 font-bold mb-2">One payment</p>
                <p className="text-6xl font-black text-white mb-6">₹{REGISTRATION_FEE}</p>
                <div className="space-y-4 text-gray-200">
                  <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                    <span className="text-gray-400">Starts</span>
                    <span className="font-semibold">5 December 2026</span>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                    <span className="text-gray-400">Length</span>
                    <span className="font-semibold">3 months</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-400">You leave with</span>
                    <span className="font-semibold text-right">A placement-ready DSA base</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-amber-400/20 bg-amber-400/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="p-6 rounded-2xl border-2 border-amber-400/70 bg-amber-400/10">
              <h2 className="text-2xl font-extrabold mb-3">No language barrier</h2>
              <p className="text-gray-200 leading-relaxed">
                Stay in C++, Java, Python, or any language you already know. We teach the idea, not a new syntax.
              </p>
            </div>
            <div className="p-6 rounded-2xl border-2 border-amber-400/70 bg-amber-400/10">
              <h2 className="text-2xl font-extrabold mb-3">Questions that get you placed</h2>
              <p className="text-gray-200 leading-relaxed">
                Every question is one companies ask in coding rounds. Finish the three months and you sit for placements ready.
              </p>
            </div>
            <div className="p-6 rounded-2xl border-2 border-white/20 bg-white/5">
              <h2 className="text-2xl font-extrabold mb-3">Pattern first, then topic</h2>
              <p className="text-gray-300 leading-relaxed">
                You learn the move — sliding window, binary search, graphs, DP — and then the topic it lives in. A new question becomes a variation, not a blank page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="syllabus" className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="text-center text-2xl sm:text-3xl font-bold mb-8">Topics</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {topics.map((topic) => (
              <div key={topic} className="px-5 py-4 rounded-xl border border-white/10 bg-white/5 font-semibold">
                {topic}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/40 via-orange-900/30 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
            The batch opens on 5 December.
          </h2>
          <p className="text-gray-300 mb-8">
            Hold your seat for ₹{REGISTRATION_FEE}. We will email the schedule before the first class.
          </p>
          {renderRegisterButton('cta')}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-16">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5">
            <h3 className="text-xl font-bold mb-2 text-amber-300">Have questions?</h3>
            <p className="text-gray-400 text-sm mb-6">Reach out and we&apos;ll help you with registration or course details.</p>
            <div className="space-y-4">
              <a href="tel:9899728375" className="flex items-center gap-3 text-gray-200 hover:text-amber-400 transition-colors">
                <span className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">📞</span>
                <span className="font-semibold">9899728375</span>
              </a>
              <a href="mailto:admin@ycodeverse.com" className="flex items-center gap-3 text-gray-200 hover:text-amber-400 transition-colors">
                <span className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">✉️</span>
                <span className="font-semibold">admin@ycodeverse.com</span>
              </a>
            </div>
          </div>

          <div className="flex flex-col items-center md:items-end text-center md:text-right">
            <YCodeThumbnail size="medium" className="mb-4" />
            <p className="text-gray-400 text-sm mb-4">YCodeverse — Code Your Future</p>
            <Link to="/courses" className="text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors">
              Explore our courses →
            </Link>
          </div>
        </div>
      </section>

      {showPaymentSuccess && paymentDetails && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPaymentSuccess(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md text-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-8 rounded-t-2xl text-center">
              <div className="text-5xl mb-3">🎉</div>
              <h2 className="text-2xl font-extrabold text-white">You&apos;re in</h2>
              <p className="text-amber-50 text-sm mt-2">DSA Crash Course · starts 5 December</p>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                <span className="text-gray-500">Course</span>
                <span className="font-semibold text-right max-w-[60%]">{paymentDetails.programName}</span>
              </div>
              <div className="flex justify-between text-sm border-b border-gray-200 pb-2">
                <span className="text-gray-500">Amount paid</span>
                <span className="font-semibold">₹{paymentDetails.amount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Transaction ID</span>
                <span className="font-mono text-xs text-right max-w-[60%] break-all">{paymentDetails.transactionId}</span>
              </div>
              <p className="text-gray-600 text-sm pt-2">
                We&apos;ll contact you at your registered email with next steps before the course starts on 5 December.
              </p>
              <button
                onClick={() => setShowPaymentSuccess(false)}
                className="w-full mt-4 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
};

export default DsaCrashCourse;
